import React from "react";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import { useTheme } from "../../contexts/ThemeContext";
import { LogOut, Menu, X, PawPrint, User } from "lucide-react";

const Header = ({
  user,
  userRole,
  isLoadingUserRole,
  mobileMenuOpen,
  setMobileMenuOpen,
}) => {
  const { themeClasses, effectiveTheme } = useTheme();

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <header
      className={`${
        effectiveTheme === "light"
          ? "bg-white border-gray-200"
          : "bg-gray-900/95 backdrop-blur-md border-cyan-500/20"
      } border-b sticky top-0 z-40`}
    >
      <div className="px-4 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg ${themeClasses.text.tertiary} ${themeClasses.cardHover} transition-colors`}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>

          {/* Logo/Title - Hidden on mobile when menu is open */}
          <div
            className={`${
              mobileMenuOpen ? "hidden" : "flex"
            } lg:flex items-center space-x-3`}
          >
            <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg flex items-center justify-center">
              <PawPrint className="w-5 h-5 text-white" />
            </div>
            <h1
              className={`text-lg sm:text-xl font-bold cyber-text ${themeClasses.text.primary}`}
            >
              PetID.Neural
            </h1>
          </div>

          {/* User Info & Actions */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* User Avatar */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-cyan-500 to-purple-600 p-0.5">
                <div
                  className={`w-full h-full rounded-full ${themeClasses.bg.secondary} flex items-center justify-center`}
                >
                  {user?.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt="Profile"
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <User
                      className={`w-4 h-4 ${themeClasses.text.secondary}`}
                    />
                  )}
                </div>
              </div>
              <div className="hidden sm:flex flex-col">
                <div className="flex items-center space-x-2">
                  <span
                    className={`text-sm font-medium ${themeClasses.text.primary}`}
                  >
                    {user?.displayName || "User"}
                  </span>
                  {!isLoadingUserRole && userRole === "admin" && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                      Admin
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`text-xs ${themeClasses.text.secondary}`}>
                    {user?.email}
                  </span>
                  {!isLoadingUserRole && userRole !== "admin" && (
                    <span
                      className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        userRole === "pro"
                          ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
                          : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300"
                      }`}
                    >
                      {userRole === "pro" ? "Pro" : "Free"}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 rounded-lg ${
                themeClasses.bg.buttonSecondary
              } ${themeClasses.text.secondary} hover:${
                themeClasses.text.primary.split(" ")[1]
              } transition-colors`}
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
